// @flow

// Types
import type {
  AssetOperation,
} from '../../types'

import type {
  Asset,
} from 'fc-model-assets/types'

import {
  toJson,
} from 'fc-decoder-asset'
import _map from 'lodash/map'
import _every from 'lodash/every'
import _size from 'lodash/size'

// Operator
import operator from 'fc-operator-asset/src/operator'

// Selectors and Actions
import { selectedSeasonId$, updateCurrentSeason } from 'hfty-seasons'

// Controller
import controller from 'fc-ctrl-assets'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

import validateOperation from './validateOperation'

// Model Properties
import {
  PROP_SEASON,
} from 'fc-model-assets'

type OperationsArgs = {
  operations$: (state: Object) => Array<AssetOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit()])
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)

  const nextOperations: Array<AssetOperation> = yield _map(operations, function (operation: AssetOperation): Object {
    return call(handleSubmitOperation, operation)
  })

  return _every(nextOperations, function (operation: AssetOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: AssetOperation): Generator<*, AssetOperation, *> {
  const validatedOperation = yield call(validateOperation, operation)

  if (_size(validatedOperation.errors)) {
    return validatedOperation
  }

  const model: Asset = toJson(operation.model)

  const proc = operation.operation === 'ADD'
    ? controller.create
    : controller.update

  const { error, response } = yield call([controller, proc], {
    model: {
      ...model,
      createSubfield: true,
    },
    queryParams: {
      season: model[PROP_SEASON],
    },
  }, {
    meta: { encode: false },
  })
  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const currentSeason = yield select(selectedSeasonId$)
    if (currentSeason !== model[PROP_SEASON]) {
      yield put(updateCurrentSeason(model[PROP_SEASON]))
    }
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)

    return nextOperation
  }
}
